import { DatePipe } from '@angular/common';
import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { SharedService } from '../../../services/shared.service';
import { MiscReportService } from '../../../services/misc-report.service';
import {
  MISC_INCIDENT_ORGANIZATION_FACILITIES,
  getMiscIncidentOrganizationNames
} from './misc-incident-organization-map';
import * as XLSX from 'xlsx-js-style';
import * as FileSaver from 'file-saver';

@Component({
  selector: 'app-misc-incident-report',
  templateUrl: './incident-report.component.html',
  styleUrls: ['./incident-report.component.css']
})
export class MiscIncidentReportComponent implements OnInit {

  /** Matches Streamlit `group_options = ["Select a group..."] + list(FACILITIES.keys())`. */
  readonly selectGroupPlaceholder = 'Select a group...';

  @ViewChild('homesWrap', { static: false })
  homesWrap: ElementRef;

  @ViewChild('homesTrigger', { static: false })
  homesTrigger: ElementRef;

  homesPanelStyle: { [key: string]: string } = {};

  /** Organization labels in fixed order (Search Filters). */
  organizationKeys: string[] = [];
  selectedOrganization = '';
  facilityOptions: { id: number; name: string }[] = [];
  selectedFacilityIds: number[] = [];
  /** Multiselect homes: one-line trigger + checkbox panel (Streamlit multiselect style). */
  homesPanelOpen = false;
  startDate: Date = null as any;
  endDate: Date = null as any;
  /** Active quick-range preset id, or '' when dates were edited manually. */
  selectedDateRangePreset = 'year';
  readonly dateRangePresets: { id: string; label: string }[] = [
    { id: 'week', label: 'Last week' },
    { id: 'month', label: 'Last month' },
    { id: '3months', label: 'Last 3 months' },
    { id: '6months', label: 'Last 6 months' },
    { id: 'year', label: 'Last year' }
  ];
  rows: any[] = [];
  loading = false;
  errorMessage = '';
  showErrorMessage = false;
  successMessage = '';

  sendEmailAfterGeneration = false;
  toEmail = '';
  ccEmails = '';
  emailSending = false;
  emailResultMessage = '';

  dateInputValue(d: Date): string {
    return this.formatDateOnlyForApi(d);
  }

  onDateInput(isStart: boolean, value: string): void {
    if (!value || value.length < 10) {
      if (isStart) {
        this.startDate = null as any;
      } else {
        this.endDate = null as any;
      }
      return;
    }
    var parts = value.split('-');
    if (parts.length !== 3) {
      return;
    }
    var y = parseInt(parts[0], 10);
    var mo = parseInt(parts[1], 10) - 1;
    var day = parseInt(parts[2], 10);
    if (isNaN(y) || isNaN(mo) || isNaN(day)) {
      return;
    }
    var next = new Date(y, mo, day);
    if (isStart) {
      this.startDate = next;
    } else {
      this.endDate = next;
    }
    this.selectedDateRangePreset = '';
  }

  private todayLocal(): Date {
    var now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  applyDateRangePreset(presetId: string): void {
    var end = this.todayLocal();
    var start: Date;
    if (presetId === 'week') {
      start = new Date(end.getFullYear(), end.getMonth(), end.getDate() - 7);
    } else if (presetId === 'month') {
      start = new Date(end.getFullYear(), end.getMonth() - 1, end.getDate());
    } else if (presetId === '3months') {
      start = new Date(end.getFullYear(), end.getMonth() - 3, end.getDate());
    } else if (presetId === '6months') {
      start = new Date(end.getFullYear(), end.getMonth() - 6, end.getDate());
    } else if (presetId === 'year') {
      start = new Date(end.getFullYear() - 1, end.getMonth(), end.getDate());
    } else {
      return;
    }
    this.startDate = start;
    this.endDate = end;
    this.selectedDateRangePreset = presetId;
  }

  formatOccurredCell(row: any): string {
    var od = this.pickRowField(row, 'occurredDate', 'OccurredDate');
    if (od == null || od === '') {
      return '';
    }
    var d = od instanceof Date ? od : new Date(od as any);
    if (isNaN(d.getTime())) {
      return String(od);
    }
    var formatted = this.datePipe.transform(d, 'MMM d, yyyy');
    return formatted != null ? formatted : '';
  }

  constructor(
    public sharedService: SharedService,
    private miscReportService: MiscReportService,
    public datePipe: DatePipe
  ) { }

  ngOnInit(): void {
    this.applyDateRangePreset('year');
    var self = this;
    this.sharedService.reloadUserPrivileges().then(function () {
      self.sharedService.loadFacilities();
      window.setTimeout(function () {
        self.initOrganizationFiltersFromMap();
      }, 250);
    });
  }

  /** Build sidebar from static org → home → Facility.Id map (no FacilityGroup API). */
  private initOrganizationFiltersFromMap(): void {
    var names = getMiscIncidentOrganizationNames();
    if (!names || names.length === 0) {
      return;
    }
    this.organizationKeys = [this.selectGroupPlaceholder].concat(names);
    this.selectedOrganization = this.selectGroupPlaceholder;
    this.onGroupChange();
  }

  /** True when a real organization is selected (not Streamlit placeholder row). */
  isOrganizationSelected(): boolean {
    return !!this.selectedOrganization && this.selectedOrganization !== this.selectGroupPlaceholder;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(ev: MouseEvent): void {
    if (!this.homesPanelOpen) {
      return;
    }
    var target = ev.target as Node;
    var wrap = this.homesWrap && this.homesWrap.nativeElement;
    if (wrap && target && wrap.contains(target)) {
      return;
    }
    this.homesPanelOpen = false;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.homesPanelOpen) {
      this.updateHomesPanelPosition();
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.homesPanelOpen) {
      this.updateHomesPanelPosition();
    }
  }

  toggleHomesPanel(): void {
    this.homesPanelOpen = !this.homesPanelOpen;
    if (this.homesPanelOpen) {
      var self = this;
      window.setTimeout(function () {
        self.updateHomesPanelPosition();
      }, 0);
    }
  }

  private updateHomesPanelPosition(): void {
    var triggerEl = this.homesTrigger && this.homesTrigger.nativeElement;
    if (!triggerEl) {
      return;
    }
    var rect = triggerEl.getBoundingClientRect();
    var maxH = Math.max(120, window.innerHeight - rect.bottom - 12);
    this.homesPanelStyle = {
      position: 'fixed',
      top: (rect.bottom + 2) + 'px',
      left: rect.left + 'px',
      width: rect.width + 'px',
      'max-height': maxH + 'px',
      'z-index': '1060'
    };
  }

  homesSummaryText(): string {
    var n = this.selectedFacilityIds.length;
    if (n === 0) {
      return 'Choose one or more homes...';
    }
    return n === 1 ? '1 home selected' : (n + ' homes selected');
  }

  isHomeSelected(id: number): boolean {
    return this.selectedFacilityIds.indexOf(id) >= 0;
  }

  toggleHome(id: number, ev: Event): void {
    var t = ev.target as HTMLInputElement;
    if (!t) {
      return;
    }
    var checked = t.checked;
    var ids = this.selectedFacilityIds.slice();
    var idx = ids.indexOf(id);
    if (checked && idx < 0) {
      ids.push(id);
    }
    if (!checked && idx >= 0) {
      ids.splice(idx, 1);
    }
    this.selectedFacilityIds = ids;
  }

  onGroupChange(): void {
    this.homesPanelOpen = false;
    this.facilityOptions = [];
    this.selectedFacilityIds = [];
    var org = this.selectedOrganization;
    if (!org || org === this.selectGroupPlaceholder) {
      return;
    }
    var homes = MISC_INCIDENT_ORGANIZATION_FACILITIES[org];
    if (!homes) {
      return;
    }
    var homeNames = Object.keys(homes);
    var j = 0;
    for (j = 0; j < homeNames.length; j++) {
      var label = homeNames[j];
      this.facilityOptions.push({ id: homes[label], name: label });
    }
  }

  getSelectedGroupName(): string {
    if (!this.selectedOrganization || this.selectedOrganization === this.selectGroupPlaceholder) {
      return '';
    }
    return this.selectedOrganization;
  }

  /** Export filename dates: 20May2025 (day + Mon + year). */
  private formatDateForFileName(d: Date): string {
    var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return d.getDate() + months[d.getMonth()] + d.getFullYear();
  }

  /** Local calendar yyyy-MM-dd (avoids UTC midnight shift when JSON-serializing Date). */
  private formatDateOnlyForApi(d: Date): string {
    if (!d) {
      return '';
    }
    var y = d.getFullYear();
    var m = d.getMonth() + 1;
    var day = d.getDate();
    return y + '-' + (m < 10 ? '0' : '') + m + '-' + (day < 10 ? '0' : '') + day;
  }

  /** IR-{code}_{code}(20May2025-20May2026).xlsx — one code per selected home. */
  buildExportFileName(): string {
    var codes = this.buildFacilityCodesForFileName();
    var startStr = this.formatDateForFileName(this.startDate);
    var endStr = this.formatDateForFileName(this.endDate);
    var codePart = codes ? codes : 'IR';
    return 'IR-' + codePart + '(' + startStr + '-' + endStr + ').xlsx';
  }

  private facilityCodeFromHomeName(homeName: string): string {
    var w = homeName.split(/\s+/);
    var s = '';
    var j = 0;
    for (j = 0; j < w.length; j++) {
      if (w[j].length > 0) {
        s += w[j].charAt(0).toUpperCase();
      }
    }
    return s;
  }

  /** Selected homes in dropdown order, codes joined with underscore. */
  private buildFacilityCodesForFileName(): string {
    var codes: string[] = [];
    var opts = this.facilityOptions;
    var ids = this.selectedFacilityIds;
    var i = 0;
    for (i = 0; i < opts.length; i++) {
      if (ids.indexOf(opts[i].id) >= 0) {
        codes.push(this.facilityCodeFromHomeName(opts[i].name));
      }
    }
    return codes.join('_');
  }

  generateReport(): void {
    this.errorMessage = '';
    this.showErrorMessage = false;
    this.successMessage = '';
    this.emailResultMessage = '';

    if (this.sendEmailAfterGeneration && (!this.toEmail || !this.toEmail.trim())) {
      this.showErrorMessage = true;
      this.errorMessage = 'Please provide a \'To\' email address before generating the report.';
      return;
    }

    /** app.py: `else: st.error("Please ensure you have selected at least one home and a full date range.")` */
    var hasHomes = this.selectedFacilityIds && this.selectedFacilityIds.length > 0;
    var hasFullRange = !!this.startDate && !!this.endDate;
    if (!this.isOrganizationSelected() || !hasHomes || !hasFullRange) {
      this.showErrorMessage = true;
      this.errorMessage = 'Please ensure you have selected at least one home and a full date range.';
      return;
    }
    if (this.endDate < this.startDate) {
      this.showErrorMessage = true;
      this.errorMessage = 'End date cannot be before start date.';
      return;
    }
    var userId = this.sharedService.getUserId();
    if (!userId) {
      return;
    }
    this.loading = true;
    this.rows = [];
    var body = {
      pharmacyId: 3, // TODO: revert to this.sharedService.pharmacyId — temporarily fixed (correct tenant is 3, not 1).
      facilityIds: this.selectedFacilityIds,
      startDate: this.formatDateOnlyForApi(this.startDate),
      endDate: this.formatDateOnlyForApi(this.endDate)
    };
    var self = this;
    this.miscReportService.postMiscReportRows(userId, body).subscribe(
      function (data) {
        self.rows = data || [];
        self.loading = false;
        if (self.rows.length > 0) {
          self.successMessage = 'Successfully retrieved ' + self.rows.length + ' records.';
        } else {
          self.successMessage = '';
          self.showErrorMessage = true;
          self.errorMessage = 'No records found for the selected criteria.';
        }
        if (self.rows.length > 0 && self.sendEmailAfterGeneration) {
          self.sendExcelByEmail(userId);
        }
      },
      function (_err) {
        self.loading = false;
        self.showErrorMessage = true;
        self.errorMessage = 'Could not load the report. Verify privileges and selected homes.';
      }
    );
  }

  private sendExcelByEmail(userId: string): void {
    var fileName = this.buildExportFileName();
    var blob = this.buildExcelBlob();
    var self = this;
    this.emailSending = true;
    this.emailResultMessage = '';
    var fd = new FormData();
    fd.append('toEmail', this.toEmail.trim());
    fd.append('ccEmails', this.ccEmails ? this.ccEmails : '');
    fd.append('fileName', fileName);
    fd.append('file', blob, fileName);
    this.miscReportService.postSendIncidentReportExcel(userId, fd).subscribe(
      function (res) {
        self.emailSending = false;
        if (res && res.success) {
          self.emailResultMessage = 'Email with reports sent successfully to ' + self.toEmail.trim() + '.';
        } else {
          self.emailResultMessage = (res && res.message) ? res.message : 'Email could not be sent.';
        }
      },
      function (_err) {
        self.emailSending = false;
        self.emailResultMessage = 'Email could not be sent. Try again or use Download only.';
      }
    );
  }

  pickRowField(row: any, camel: string, pascal: string): any {
    if (!row) {
      return '';
    }
    if (Object.prototype.hasOwnProperty.call(row, camel)) {
      return row[camel];
    }
    return row[pascal];
  }

  /** Column headers matching reference export (IR-HC*.xlsx). */
  private excelExportColumnHeaders(): string[] {
    return [
      'HomeName',
      'IncidentReportId',
      'OccurredDate',
      'Resident',
      'IncidentDescription',
      'Origin',
      'ResidentOutcome',
      'IncidentType',
      'MedicationsInvolved',
      'HighAlertMedsInvolved',
      'DoctorName',
      'ContributingFactor',
      'HomeResponse',
      'PharmacyResponse'
    ];
  }

  private excelCellValue(val: any): any {
    if (val == null || val === undefined) {
      return '';
    }
    return val;
  }

  private excelIncidentReportId(row: any): number | string {
    var v = this.pickRowField(row, 'incidentReportId', 'IncidentReportId');
    if (v == null || v === '') {
      return '';
    }
    var n = parseInt(String(v), 10);
    if (!isNaN(n)) {
      return n;
    }
    return v;
  }

  /** Occurred date with time, same as reference workbook (YYYY-MM-DD HH:MM:SS). */
  private parseOccurredDateForExcel(row: any): Date | string {
    var od = this.pickRowField(row, 'occurredDate', 'OccurredDate');
    if (od == null || od === '') {
      return '';
    }
    var d = od instanceof Date ? od : new Date(od as any);
    if (isNaN(d.getTime())) {
      return String(od);
    }
    return d;
  }

  /** Header / zebra colors (blue family, matches reference export). */
  private readonly miscExcelHeaderFill = '2F5496';
  private readonly miscExcelHeaderFont = 'FFFFFF';
  private readonly miscExcelOddRowFill = 'D6E4F0';

  private miscExcelWrapAlign(): any {
    return { vertical: 'top', wrapText: true };
  }

  private miscExcelHeaderStyle(): any {
    return {
      fill: { patternType: 'solid', fgColor: { rgb: this.miscExcelHeaderFill } },
      font: { bold: true, color: { rgb: this.miscExcelHeaderFont } },
      alignment: this.miscExcelWrapAlign()
    };
  }

  private miscExcelTitleStyle(): any {
    return {
      font: { bold: true },
      alignment: this.miscExcelWrapAlign()
    };
  }

  private miscExcelOddDataRowStyle(): any {
    return {
      fill: { patternType: 'solid', fgColor: { rgb: this.miscExcelOddRowFill } },
      alignment: this.miscExcelWrapAlign()
    };
  }

  private miscExcelDefaultDataStyle(): any {
    return {
      alignment: this.miscExcelWrapAlign()
    };
  }

  private applyMiscExcelSheetLayout(worksheet: XLSX.WorkSheet, columnCount: number, totalRowCount: number, dataRowCount: number): void {
    worksheet['!cols'] = [
      { wch: 28 },
      { wch: 18 },
      { wch: 21 },
      { wch: 24 },
      { wch: 50 },
      { wch: 10 },
      { wch: 42 },
      { wch: 31 },
      { wch: 50 },
      { wch: 23 },
      { wch: 21 },
      { wch: 50 },
      { wch: 50 },
      { wch: 50 }
    ];

    var lastColLetter = XLSX.utils.encode_col(columnCount - 1);
    worksheet['!autofilter'] = { ref: 'A2:' + lastColLetter + totalRowCount };

    if (!worksheet['!ref']) {
      return;
    }
    var range = XLSX.utils.decode_range(worksheet['!ref']);
    var occurredCol = 2;
    var firstDataRowIndex = 2;
    var R = 0;
    var C = 0;
    for (R = range.s.r; R <= range.e.r; R++) {
      var excelRowNum = R + 1;
      var rowStyle: any = null;
      if (excelRowNum === 1) {
        rowStyle = this.miscExcelTitleStyle();
      } else if (excelRowNum === 2) {
        rowStyle = this.miscExcelHeaderStyle();
      } else if (excelRowNum % 2 === 1) {
        rowStyle = this.miscExcelOddDataRowStyle();
      } else {
        rowStyle = this.miscExcelDefaultDataStyle();
      }
      for (C = range.s.c; C <= range.e.c; C++) {
        var addr = XLSX.utils.encode_cell({ r: R, c: C });
        var cell = worksheet[addr];
        if (!cell) {
          cell = { t: 's', v: '' };
          worksheet[addr] = cell;
        }
        cell.s = rowStyle;
        if (R >= firstDataRowIndex && C === occurredCol && (cell.t === 'd' || cell.t === 'n')) {
          cell.z = 'yyyy-mm-dd hh:mm:ss';
        }
      }
    }
  }

  private buildExcelBlob(): Blob {
    var groupName = this.getSelectedGroupName();
    var titleText = groupName ? 'Incident Report - ' + groupName : 'Incident Report';
    var headers = this.excelExportColumnHeaders();
    var excelRows: any[][] = [];
    excelRows.push([titleText]);
    excelRows.push(headers);
    var r = 0;
    for (r = 0; r < this.rows.length; r++) {
      var row = this.rows[r];
      excelRows.push([
        this.excelCellValue(this.pickRowField(row, 'name', 'Name')),
        this.excelIncidentReportId(row),
        this.parseOccurredDateForExcel(row),
        this.excelCellValue(this.pickRowField(row, 'resident', 'Resident')),
        this.excelCellValue(this.pickRowField(row, 'incidentDescription', 'IncidentDescription')),
        this.excelCellValue(this.pickRowField(row, 'origin', 'Origin')),
        this.excelCellValue(this.pickRowField(row, 'residentOutcome', 'ResidentOutcome')),
        this.excelCellValue(this.pickRowField(row, 'incidentType', 'IncidentType')),
        this.excelCellValue(this.pickRowField(row, 'medicationInvolved', 'MedicationInvolved')),
        this.excelCellValue(this.pickRowField(row, 'highAlertMedsInvolved', 'HighAlertMedsInvolved')),
        this.excelCellValue(this.pickRowField(row, 'doctorName', 'DoctorName')),
        this.excelCellValue(this.pickRowField(row, 'contributingFactor', 'ContributingFactor')),
        this.excelCellValue(this.pickRowField(row, 'homeResponse', 'HomeResponse')),
        this.excelCellValue(this.pickRowField(row, 'pharmacyResponse', 'PharmacyResponse'))
      ]);
    }
    var rawName = groupName ? groupName + ' Report' : 'Incident Report';
    var sheetName = rawName.length > 31 ? rawName.substring(0, 31) : rawName;
    var columnCount = headers.length;
    var totalRowCount = excelRows.length;
    var worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(excelRows, { cellDates: true });
    this.applyMiscExcelSheetLayout(worksheet, columnCount, totalRowCount, this.rows.length);
    var workbook: XLSX.WorkBook = {
      Sheets: {},
      SheetNames: []
    };
    workbook.Sheets[sheetName] = worksheet;
    workbook.SheetNames.push(sheetName);
    var excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array', cellDates: true });
    var excelType =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    return new Blob([excelBuffer], { type: excelType });
  }

  downloadExcel(): void {
    if (!this.rows || this.rows.length === 0) {
      return;
    }
    var fileName = this.buildExportFileName();
    var blob = this.buildExcelBlob();
    FileSaver.saveAs(blob, fileName);
  }
}
